import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  AdaptedGoalTransferDTO,
  GoalTransferType,
} from '@/domain/transfer/transferTypes'
import { useGetTransactionStatusColorAndLabel } from '@/hooks/useGetTransactionStatusColorAndLabel'
import { formatAddress, generateRandomId } from '@/utils'

export interface FormattedSavingTransfer {
  id: string
  label: string
  value: string
  secondaryValue?: string
  isVisible: boolean
  showSeparator?: boolean
  separatorGap?: number
  copyValue?: string
  isCopyable?: boolean
  valueProps?: { color?: string }
}

interface UseFormatSavingTransferProps {
  goalTransfer?: Partial<AdaptedGoalTransferDTO>
}

export const useFormatSavingTransfer = ({
  goalTransfer,
}: UseFormatSavingTransferProps): FormattedSavingTransfer[] => {
  const { t } = useTranslation()

  const { color: statusColor, label: statusLabel } =
    useGetTransactionStatusColorAndLabel(goalTransfer?.status)

  return useMemo(() => {
    const renderWhenIs = (list: GoalTransferType[]) => {
      if (!goalTransfer?.type) return true
      return list.includes(goalTransfer.type)
    }

    return [
      {
        id: generateRandomId(),
        label: t('translation:amount'),
        value: goalTransfer?.formattedCryptoAmount || '0',
        secondaryValue: goalTransfer?.formattedFiatAmount
          ? `≈${goalTransfer.formattedFiatAmount}`
          : undefined,
        isVisible: true,
        showSeparator: true,
      },
      {
        id: generateRandomId(),
        label: t('transactions:rowItemLabels.transactionFee'),
        value: goalTransfer?.formattedFeeCryptoAmount || '0',
        isVisible: renderWhenIs(['transfer_out']),
        showSeparator: true,
      },
      {
        id: generateRandomId(),
        label: t('translation:totalWithdrawal'),
        value: goalTransfer?.formattedCryptoAmountMinusFee || '0',
        secondaryValue: goalTransfer?.formattedFiatAmountMinusFee
          ? `≈${goalTransfer.formattedFiatAmountMinusFee}`
          : undefined,
        isVisible: renderWhenIs(['transfer_out']),
        showSeparator: true,
      },
      {
        id: generateRandomId(),
        label: t('translation:date'),
        value: goalTransfer?.formattedDateWithTime || '',
        isVisible: true,
        showSeparator: true,
        separatorGap: 20,
      },
      {
        id: generateRandomId(),
        label: t('translation:transactionId'),
        value: formatAddress(goalTransfer?.transaction_id || ''),
        copyValue: goalTransfer?.transaction_id || '',
        isVisible: !!goalTransfer?.transaction_id,
        showSeparator: true,
        separatorGap: 20,
        isCopyable: true,
      },
      {
        id: generateRandomId(),
        label: t('translation:status'),
        value: statusLabel || '',
        valueProps: { color: statusColor },
        isVisible: true,
        showSeparator: true,
        separatorGap: 20,
      },
    ]
  }, [goalTransfer, statusColor, statusLabel, t])
}
